import { Injectable, inject } from '@angular/core';
import { of, map, Observable } from 'rxjs';
import { BaseHttpService } from '@app/core/services/base-http.service';
import { JwtTokenModel } from '@app/core/models/jwt-token.model';
import { Router } from '@angular/router';
import { UserRoles } from '../models/common.model';

@Injectable({
  providedIn: 'root'
})
export class AuthService extends BaseHttpService {
  private _router = inject(Router);

  get UserName() {
    return this.loadUserName();
  }

  get Role() {
    return this.loadUserRole();
  }

  get Country() {
    return this.loadUserCountry();
  }

  get IsFranchiseeUser() {
    const role = this.loadUserRole();
    return role === UserRoles.FranchiseeAdmin || role === UserRoles.FranchiseeUser;
  }

  get IsAddCarUser() {
    const role = this.loadUserRole();
    return role === UserRoles.AddCarAdmin || role === UserRoles.AddCarUser;
  }

  constructor() {
    super();
  }

  login(userName: string, password: string, remember: boolean): Observable<void> {
    const body = JSON.stringify({ userName, password });
    const options = this.buildOptions();

    return this.httpClient.post<JwtTokenModel>(`${this.baseApiUrl}/api/auth/login`, body, options).pipe(
      map((response) => {
        this.storeRemember(remember);
        this.storeUserName(userName);
        this.storeJwtToken(response);
        this._router.navigate(['']);
      })
    );
  }

  logout(): Observable<void> {
    const jwtToken = this.loadJwtToken();
    if (!jwtToken) return of();

    return this.httpClient.delete<void>(`${this.baseApiUrl}/api/auth/logout/${jwtToken.refresh_token}`).pipe(
      map(() => {
        this.clearStorages();
        this._router.navigate(['/login']);
      })
    );
  }

  refreshToken(): Observable<void> {
    const jwtToken = this.loadJwtToken();
    if (!jwtToken) return of();

    return this.httpClient
      .post<JwtTokenModel>(`${this.baseApiUrl}/api/auth/refresh/${jwtToken.refresh_token}`, null)
      .pipe(
        map((tokens) => {
          this.storeJwtToken(tokens);
        })
      );
  }

  getAuthToken() {
    return this.loadJwtToken()?.auth_token;
  }

  isAuthenticated() {
    return !!this.loadJwtToken();
  }

  private loadUserName() {
    let userName: string | null = null;

    if (this.loadRemember()) {
      const localStorageData = localStorage.getItem('user-name');
      if (localStorageData) {
        userName = JSON.parse(localStorageData);
      }
    } else {
      const sessionStorageData = sessionStorage.getItem('user-name');
      if (sessionStorageData) {
        userName = JSON.parse(sessionStorageData);
      }
    }

    return userName;
  }

  private storeUserName(userName: string) {
    if (this.loadRemember()) {
      this.storeUserNameToLocalStorage(userName);
    } else {
      this.storeUserNameToSessionStorage(userName);
    }
  }

  private storeUserNameToLocalStorage(userName: string) {
    localStorage.setItem('user-name', JSON.stringify(userName));
  }

  private storeUserNameToSessionStorage(userName: string) {
    sessionStorage.setItem('user-name', JSON.stringify(userName));
  }

  private loadUserRole() {
    return this.loadJwtToken()?.role;
  }

  private loadRemember() {
    return Boolean(localStorage.getItem('remember'));
  }

  private loadUserCountry() {
    return this.loadJwtToken()?.country_uid;
  }

  private storeRemember(remember: boolean) {
    if (remember) {
      localStorage.setItem('remember', '1');
    }
  }

  private loadJwtToken() {
    let jwtToken: JwtTokenModel | null = null;

    if (this.loadRemember()) {
      const localStorageData = localStorage.getItem('jwt-token');
      if (localStorageData) {
        jwtToken = JSON.parse(localStorageData);
      }
    } else {
      const sessionStorageData = sessionStorage.getItem('jwt-token');
      if (sessionStorageData) {
        jwtToken = JSON.parse(sessionStorageData);
      }
    }

    return jwtToken;
  }

  private storeJwtToken(jwtToken: JwtTokenModel) {
    if (this.loadRemember()) {
      this.storeJwtTokenToLocalStorage(jwtToken);
    } else {
      this.storeJwtTokenToSessionStorage(jwtToken);
    }
  }

  private storeJwtTokenToLocalStorage(jwtToken: JwtTokenModel) {
    localStorage.setItem('jwt-token', JSON.stringify(jwtToken));
  }

  private storeJwtTokenToSessionStorage(jwtToken: JwtTokenModel) {
    sessionStorage.setItem('jwt-token', JSON.stringify(jwtToken));
  }

  private clearStorages() {
    const keys = ['user-name', 'jwt-token', 'remember'];
    keys.forEach((key) => {
      sessionStorage.removeItem(key);
      localStorage.removeItem(key);
    });
  }
}
